import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { buildContractHoursBar } from './contractHoursSegments';
import type { ContractHoursSegmentKind } from './contractHoursSegments';
import type { ContractStaffing } from './contractStaffing';
import { HOURS_LEGEND_CHIP, HOURS_SEGMENT_SX } from './hoursBarStyles';

import { FigureTooltip } from '@/components/ui';
import { formatHours } from '@/lib';

/**
 * The contract's hours in the task panel's Contract insights (3 Nov, Mette):
 * styled like the task's Hours at the top of the panel — the contract's hours
 * large with their caption, then one bar and a legend carrying the hours —
 * with the allocated share as a number on the figure's line, where the
 * Allocated box used to say it. See `contractHoursSegments.ts` for the
 * segments.
 *
 * Considered and not reused: the task bar's markup lives inline in
 * `TaskInsightPanel` with its own legend rule (logged never listed); the
 * colours are shared (`hoursBarStyles.ts`), the markup is small enough to
 * repeat. The dashed outline for hours not on any task is the old cascade's
 * "not reached" outline.
 */
export type ContractHoursBarProps = { staffing: ContractStaffing };

/** The contract-only segment: drawn as an outline, so it reads as room, not work. */
const NOT_ON_TASKS_SX = {
  boxSizing: 'border-box',
  border: 'var(--border-width-thin) dashed var(--border-subtle)',
  borderLeft: 'none',
  borderTopRightRadius: 'var(--border-radius-pill)',
  borderBottomRightRadius: 'var(--border-radius-pill)',
} as const;

const SEGMENT_SX: Record<ContractHoursSegmentKind, object> = {
  allocated: HOURS_SEGMENT_SX.allocated,
  unallocated: HOURS_SEGMENT_SX.unallocated,
  notOnTasks: NOT_ON_TASKS_SX,
  over: HOURS_SEGMENT_SX.over,
};

const LEGEND_CHIP_SX: Record<ContractHoursSegmentKind, object> = {
  allocated: { bgcolor: HOURS_LEGEND_CHIP.allocated },
  unallocated: { bgcolor: HOURS_LEGEND_CHIP.unallocated },
  notOnTasks: {
    boxSizing: 'border-box',
    border: 'var(--border-width-thin) dashed var(--border-subtle)',
  },
  over: { bgcolor: HOURS_LEGEND_CHIP.over },
};

const LEGEND_LABEL_KEY = {
  allocated: 'pmCommon:term.Allocated',
  unallocated: 'pmPlanningPanel:taskInsight.segment.unallocated',
  notOnTasks: 'pmPlanningPanel:contractHours.NotOnTasks',
  over: 'pmPlanningPanel:taskInsight.segment.over',
} as const satisfies Record<ContractHoursSegmentKind, string>;

/** The task Hours figure's type: the two numbers on the line match it. */
const FIGURE_SX = {
  fontFamily: 'var(--font-family-mono)',
  fontSize: 24,
  fontWeight: 500,
  color: 'var(--fg-1)',
  lineHeight: 1,
} as const;

const CAPTION_SX = { fontSize: 11, color: 'var(--fg-3)' } as const;

export const ContractHoursBar = ({ staffing }: ContractHoursBarProps) => {
  const { t } = useTranslation(['pmPlanningPanel', 'pmCommon', 'pmFinancials']);
  const bar = buildContractHoursBar(staffing);
  const overAllocated = staffing.allocatedPct !== null && staffing.allocatedPct > 100;

  return (
    <Box data-automation-id="task-contract-hours" sx={{ mb: '14px' }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
        <FigureTooltip title={t('contractHours.help.Contract')}>
          <Box
            tabIndex={0}
            data-automation-id="task-contract-hours-budget"
            sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}
          >
            <Box component="span" data-automation-id="task-contract-hours-value" sx={FIGURE_SX}>
              {formatHours(staffing.budgetHours)}
            </Box>
            <Box component="span" sx={CAPTION_SX}>
              {t('contractHours.ContractHoursBudget')}
            </Box>
          </Box>
        </FigureTooltip>
        <Box sx={{ flex: 1 }} />
        <FigureTooltip title={t('pmFinancials:help.AllocatedHours')}>
          <Box
            tabIndex={0}
            data-automation-id="task-contract-hours-allocated"
            sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}
          >
            <Box
              component="span"
              data-automation-id="task-contract-hours-allocated-pct"
              sx={{ ...FIGURE_SX, color: overAllocated ? 'var(--color-error-80)' : 'var(--fg-1)' }}
            >
              {staffing.allocatedPct === null
                ? '—'
                : `${String(Math.round(staffing.allocatedPct))}%`}
            </Box>
            <Box component="span" sx={CAPTION_SX}>
              {t('pmCommon:term.Allocated')}
            </Box>
          </Box>
        </FigureTooltip>
      </Box>
      {bar ? (
        <>
          <Box
            data-automation-id="task-contract-hours-bar"
            sx={{
              height: 6,
              borderRadius: 'var(--border-radius-pill)',
              overflow: 'hidden',
              display: 'flex',
              mb: '6px',
            }}
          >
            {bar.segments.map((segment) => (
              <Box
                key={segment.kind}
                data-automation-id={`task-contract-hours-segment-${segment.kind}`}
                sx={{
                  height: '100%',
                  width: `${segment.pct}%`,
                  flex: 'none',
                  ...SEGMENT_SX[segment.kind],
                  transition: 'width var(--duration-slow) var(--easing-standard)',
                }}
              />
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {bar.legend.map((entry) => (
              <Box
                key={entry.kind}
                component="span"
                data-automation-id={`task-contract-hours-legend-${entry.kind}`}
                sx={{
                  fontSize: 10,
                  color: entry.kind === 'over' ? 'var(--color-coral-100)' : 'var(--fg-3)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  whiteSpace: 'nowrap',
                  flex: 'none',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '2px',
                    display: 'inline-block',
                    flex: 'none',
                    ...LEGEND_CHIP_SX[entry.kind],
                  }}
                />
                {t('taskInsight.LegendEntry', {
                  label: t(LEGEND_LABEL_KEY[entry.kind]),
                  hours: formatHours(entry.hours),
                })}
              </Box>
            ))}
          </Box>
        </>
      ) : null}
    </Box>
  );
};
